import { origin } from './userinteraction.config';
import * as signalR from '@microsoft/signalr';
import * as utils from '../utils/utils.js';

export default class UserInteractionService {
    constructor($rootScope, $log) {
        this.$rootScope = $rootScope;
        this.$log = $log;
        this.hub = '/interaction';

        this.SIGNALR_STATUS = Object.freeze({
            INITIALIZED: 'Initialized',
            DISCONNECTED: 'Disconnected',
            CONNECTING: 'Connecting',
            CONNECTED: 'Connected',
            STOPPED: 'Stopped'
        });

        this.connectionStatus = this.SIGNALR_STATUS.DISCONNECTED;
    }

    $onDestroy() {
        this.unsubscribe();
        this.stop(() => {
            console.info('User intraction hub stopped');
        });
    }

    /**
     * Establishes the connection with the SignalR hub
     * @param cb
     */
    initConnection() {
        let urlMatch = document.location.pathname.match(/(?!=\/)(vcl[a-z0-9]{6})(?=\/|$)/gi);

        if (urlMatch === null)
            urlMatch = document.location.search.match(/v=(vcl[a-z0-9]{6})(?=\/|$)/i);

        const eventId = urlMatch[urlMatch.length - 1]; // Use the last match if someone insists on naming a channel like an access key...

        let useCustomTransport = false;
        let transport = null;

        // Possibility to debug SignalR by specifying transport in a query string
        const qsMatch = window.location.search.match(/signalr=(.+?)(?:&|$)/i);
        if (qsMatch && qsMatch.length === 2) {
            switch (qsMatch[1]) {
                case 'ws':
                    transport = signalR.HttpTransportType.WebSockets;
                    useCustomTransport = true;
                    break;
                case 'lp':
                    transport = signalR.HttpTransportType.LongPolling;
                    useCustomTransport = true;
                    break;
                case 'sse':
                    transport = signalR.HttpTransportType.ServerSentEvents;
                    useCustomTransport = true;
                    break;
                default:
                    break;
            }
        }

        if (useCustomTransport) {
            utils.log(
                'userinteraction',
                'Available SignalR transports: ' + JSON.stringify(signalR.HttpTransportType),
                true,
                1
            );
            utils.log(
                'userinteraction',
                'SignalR transport used: ' + transport.toString(),
                true,
                1
            );
            this.connection = new signalR.HubConnectionBuilder()
                .withUrl(origin + `${this.hub}?eventid=${eventId}`, {
                    transport: transport
                })
                .withAutomaticReconnect([0, 10000, 10000, 10000, 10000, 10000])
                .configureLogging(signalR.LogLevel.Information)
                .build();
        } else {
            this.connection = new signalR.HubConnectionBuilder()
                .withUrl(origin + `${this.hub}?eventid=${eventId}`)
                .withAutomaticReconnect([0, 10000, 10000, 10000, 10000, 10000])
                .configureLogging(signalR.LogLevel.Information)
                .build();
        }

        this.on('updateUserInfo', user => {
            const { isEventAdmin, displayName, userType, userEmail } = user;
            this.signalRuser = { isEventAdmin, displayName, userType, userEmail };
            this.displayName = this.signalRuser.displayName;
            this.userEmail = this.signalRuser.userEmail;
        });

        this.connectionStatus = this.SIGNALR_STATUS.INITIALIZED;

        this.connection.onreconnecting(() => {
            console.assert(this.connection.state === signalR.HubConnectionState.Reconnecting);
            this.connectionStatus = this.SIGNALR_STATUS.CONNECTING;
            this.$rootScope.$apply();
        });

        this.connection.onreconnected(() => {
            console.assert(this.connection.state === signalR.HubConnectionState.Connected);
            this.connectionStatus = this.SIGNALR_STATUS.CONNECTED;
            this.$rootScope.$apply();
        });

        this.connection.onclose(() => {
            console.assert(this.connection.state === signalR.HubConnectionState.Disconnected);
            this.connectionStatus = this.SIGNALR_STATUS.DISCONNECTED;
            // Set a global state for external error handlign
            if (window.appInsights !== undefined && window.appInsights !== null)
                // Due to cookie settings
                window.appInsights.trackException({
                    exception: new Error('SignalR failed to reconnect')
                });
            this.$rootScope.$apply(() => {
                if (this.connectionStatus === this.SIGNALR_STATUS.DISCONNECTED) {
                    this.reconnectTimer = setInterval(() => {
                        console.log('Reconnect timer running, status: ' + this.connectionStatus);
                        if (this.connectionStatus === this.SIGNALR_STATUS.DISCONNECTED) {
                            if (navigator.onLine) {
                                this.start();
                            }
                        } else {
                            // If status is anything other than disconnected, abort the reconnect attempts.
                            clearInterval(this.reconnectTimer);
                        }
                    }, 10000);
                }
            });
        });
    }

    start() {
        if (
            this.connectionStatus === this.SIGNALR_STATUS.CONNECTED ||
            this.connectionStatus === this.SIGNALR_STATUS.CONNECTING
        ) {
            this.$log.info('Service is already started');
            return;
        }
        if (!this.connection) {
            this.initConnection();
        }
        if (this.connection.state === signalR.HubConnectionState.Disconnected) {
            this.connection.start().then(
                () => {
                    this.$log.info('Userinteraction service started');
                    this.connectionStatus = this.SIGNALR_STATUS.CONNECTED;
                },
                err => {
                    this.connectionStatus = this.SIGNALR_STATUS.DISCONNECTED;
                    this.$log.error(err);
                    if (window.appInsights !== undefined && window.appInsights !== null) {
                        // Due to cookie settings
                        window.appInsights.trackException({
                            exception: new Error(
                                'SignalR initial load failed on page request: ' + err
                            )
                        });
                    }
                }
            );
        }
    }
    // The stop method is not tested, no idea if it will work. -ahammer1
    stop(fnSuccess, fnError) {
        this.connection.stop().then(
            () => {
                this.connectionStatus = this.SIGNALR_STATUS.STOPPED;
                this.$log.info('SignalR stopped');
                this.connection = undefined;
                this.$rootScope.$apply(() => {
                    if (fnSuccess) {
                        fnSuccess();
                    }
                });
            },
            err => {
                this.$log.error(err);
                if (window.appInsights !== undefined && window.appInsights !== null)
                    //Due to cookie settings
                    window.appInsights.trackException({
                        exception: new Error('SignalR stop failed: ' + err)
                    });
                this.$rootScope.$apply(() => {
                    if (fnError) {
                        fnError();
                    }
                });
            }
        );
    }

    /**
     * Handles events sent from the hub to the client
     * @param eventName
     * @param cb
     */
    on(eventName, cb) {
        this.connection.on(eventName, (one, two, three, four, five, six) => {
            this.$rootScope.$apply(() => {
                if (cb) {
                    cb(one, two, three, four, five, six);
                }
            });
        });
    }

    /**
     * Handles events sent from the client to the server
     * @param methodName
     * @param obj
     * @param cb
     */
    invoke(methodName, obj, cb) {
        this.connection.invoke(methodName, ...obj).then(result => {
            this.$rootScope.$apply(() => {
                if (cb) {
                    cb(result);
                }
            });
        });
    }

    /**
     * The user name is entered by the user. It is sent to the
     * chat hub and persisted in the backend.
     */
    setDisplayname() {
        if (this.displayName) {
            this.signalRuser.displayName = this.displayName;
        }
        if (this.userEmail) {
            this.signalRuser.userEmail = this.userEmail;
        }
        this.invoke('setDisplayName', [this.signalRuser.displayName, this.signalRuser.userEmail]);
    }
}

UserInteractionService.$inject = ['$rootScope', '$log'];
